import { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { REPORT_SECTIONS, type SectionId } from '../ProgressReport/flow';
import { loadPrintSections, savePrintSections } from './listState';
import { LIST } from './tokens';

type StateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type PendingPrint = { owner: string; chosen: Set<SectionId> };

export function sectionIds(chosen: ReadonlySet<SectionId>): SectionId[] {
  return REPORT_SECTIONS.map((section) => section.id).filter((id) => chosen.has(id));
}

export function handedSections(state: unknown): Set<SectionId> | null {
  const handed = (state as { sections?: unknown } | null)?.sections;
  if (!Array.isArray(handed)) return null;
  const ids = REPORT_SECTIONS.map((section) => section.id).filter((id) => handed.includes(id));
  return ids.length === 0 ? null : new Set(ids);
}

export function usePrintSections(identity: () => Promise<string>, storage?: StateStorage | null) {
  const [pending, setPending] = useState<PendingPrint | null>(null);
  const load = (owner: string) => (storage === undefined ? loadPrintSections(owner) : loadPrintSections(owner, storage));
  return {
    pending,
    open: () => {
      void identity().then((owner) => setPending({ owner, chosen: load(owner) }));
    },
    cancel: () => setPending(null),
    save: (chosen: Set<SectionId>) => {
      if (pending !== null) {
        if (storage === undefined) savePrintSections(pending.owner, chosen);
        else savePrintSections(pending.owner, chosen, storage);
      }
      setPending(null);
    },
    load,
  };
}

const row = { display: 'flex', alignItems: 'center', gap: '12px', height: 40, px: '16px', fontFamily: LIST.font, fontSize: 16, lineHeight: '24px', color: LIST.ink } as const;
const box = { width: 20, height: 20, m: 0, flexShrink: 0, accentColor: LIST.primary } as const;

export function PrintSectionsDialog({
  open,
  title,
  groupLabel,
  primaryLabel,
  chosen,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  groupLabel: string;
  primaryLabel: string;
  chosen: ReadonlySet<SectionId>;
  onCancel: () => void;
  onConfirm: (chosen: Set<SectionId>) => void;
}) {
  const [ticked, setTicked] = useState(() => new Set(chosen));
  const toggle = (id: SectionId) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <Dialog open={open} onClose={onCancel} className="report-dialog" aria-labelledby="print-sections-title" PaperProps={{ sx: { borderRadius: '12px', width: 480, maxWidth: '100%', m: 2 } }}>
      <DialogTitle id="print-sections-title" sx={{ fontFamily: LIST.font, fontSize: 20, lineHeight: '28px', fontWeight: 700, color: LIST.ink, p: '24px 24px 16px' }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: '0 8px 8px', display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ px: '16px', py: '4px', fontFamily: LIST.font, fontSize: 14, lineHeight: '20px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: LIST.muted }}>
          {groupLabel}
        </Typography>
        <Box component="label" sx={{ ...row, color: LIST.disabledFg }}>
          <Box component="input" type="checkbox" checked disabled readOnly aria-label="Student details" sx={box} />
          <Box component="span" sx={{ flex: 1 }}>Student details</Box>
          <Box component="span" sx={{ fontSize: 14, lineHeight: '20px' }}>Always</Box>
        </Box>
        {REPORT_SECTIONS.map((section) => (
          <Box key={section.id} component="label" sx={{ ...row, cursor: 'pointer' }}>
            <Box component="input" type="checkbox" checked={ticked.has(section.id)} onChange={() => toggle(section.id)} sx={{ ...box, cursor: 'pointer' }} />
            <Box component="span" sx={{ flex: 1 }}>{section.label}</Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px 24px', gap: '8px' }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none', fontFamily: LIST.font, fontWeight: 600, fontSize: 16, color: LIST.primary }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={ticked.size === 0}
          onClick={() => onConfirm(new Set(ticked))}
          sx={{
            textTransform: 'none',
            fontFamily: LIST.font,
            fontWeight: 600,
            fontSize: 16,
            height: 40,
            px: '16px',
            borderRadius: '8px',
            bgcolor: LIST.primary,
            '&.Mui-disabled': { bgcolor: LIST.disabledBg, color: LIST.disabledFg },
          }}
        >
          {primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
